<script setup lang="ts">
import OrderOverviewSelection from './OrderOverviewSelection.vue';
import CheckoutSidebarCard from './CheckoutSidebarCard.vue';
import { formatPrice } from '@/utils/formatting/currencyFormatter';
import { $t } from '@/composables/translations/useTranslate';
import { sanitizeInlineHtml } from '@/utils/sanitizeHtml';

interface ServiceItem {
  name: string;
  label?: string;
  price?: number;
  nameLabel?: string;
  included: boolean;
}

interface Props {
  title?: string;
  services?: ServiceItem[];
  totalPrice?: number;
  note?: string;
  showEditButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  services: () => [],
  totalPrice: undefined,
  note: undefined,
  showEditButton: false,
});

const emit = defineEmits<{
  edit: [];
}>();

function handleEdit() {
  emit('edit');
}
</script>

<template>
  <CheckoutSidebarCard
    :show-edit-button="props.showEditButton"
    :rounded-top="true"
    wrapper-class="mt-2.5 lg:max-w-[435px]"
    @edit="handleEdit"
  >
    <p v-if="props.services.length > 0" class="text-body-small mb-[5px]">
      {{ props.title || $t('ibe.checkout.services-summary.title', 'Additional Services') }}
    </p>

    <OrderOverviewSelection
      v-for="(service, index) in props.services"
      :key="`service-${index}`"
      :name="service.name"
      :label="service.label"
      :price="service.price"
      :name-label="service.nameLabel"
      :included="service.included"
    />

    <p v-if="props.totalPrice !== undefined" class="mb-5 ml-auto font-bold">
      {{ $t('ibe.checkout.services-summary.total', 'Total:') }}
      {{ formatPrice(props.totalPrice, true) }}
    </p>

    <div
      v-if="props.note"
      class="rounded-xl px-2.5 py-3.5 bg-highlight-2 flex items-start gap-2.5 mb-7.5"
    >
      <i class="rotate-180 bg-black h-9 w-15 icon icon-info"></i>
      <!-- note may contain inline HTML (links, emphasis) — sanitized before render -->
      <p class="text-body note-content" v-html="sanitizeInlineHtml(props.note)"></p>
    </div>
  </CheckoutSidebarCard>
</template>

<style scoped>
/* Links inside the note come from sanitized v-html, so style them via :deep.
   Colours hardcoded (VueApp has no Tailwind theme() at build time). */
.note-content :deep(a) {
  color: #f54b25; /* actions-primary */
  text-decoration: underline;
  font-weight: 600;
}

.note-content :deep(a:hover) {
  color: #dc4321; /* actions-primary-hover */
}
</style>
